import { Popover } from 'bootstrap';
import { createApp } from 'vue';

// Shared state to track active popover
let activePopover = null;
let clickOutsideHandler = null;

export const popoverDirective = {
    mounted(el, binding) {
        // Check if binding.value is a component configuration
        const isComponent = binding.value && typeof binding.value === 'object' && binding.value.component;
        
        // Get options from binding or element attributes
        const options = {
            content: isComponent ? '<div class="popover-content-placeholder"></div>' : (typeof binding.value === 'string' ? binding.value : (el.getAttribute('data-bs-content') || '')),
            title: el.getAttribute('data-bs-title') || '',
            placement: binding.arg || el.getAttribute('data-bs-placement') || 'top',
            trigger: el.getAttribute('data-bs-trigger') || 'click',
            html: el.getAttribute('data-bs-html') === 'true' || true, // Enable HTML for component content
            delay: el.getAttribute('data-bs-delay') ? parseInt(el.getAttribute('data-bs-delay')) : 0,
            sanitize: false // Allow component content
        };

        // Create the popover instance
        const popover = new Popover(el, options);

        // Store popover instance and component config on element for cleanup
        el._popover = popover;
        el._popoverComponent = isComponent ? binding.value : null;

        // Add event listener to handle closing other popovers
        el.addEventListener('shown.bs.popover', () => {
            // Close the previously active popover if it exists and is different
            if (activePopover && activePopover !== popover) {
                activePopover.hide();
            }
            activePopover = popover;
            
            // Mount Vue component if needed
            if (isComponent) {
                const popoverBody = popover.tip.querySelector('.popover-content-placeholder');
                if (popoverBody) {
                    const app = createApp(binding.value.component, {
                        ...binding.value.props,
                        onCopyAddress: binding.value.onCopyAddress
                    });
                    
                    // Mount the component and store both app and component instance
                    el._popoverApp = app;
                    el._popoverComponent = app.mount(popoverBody);
                    
                    // Force popover to recalculate position after content is rendered
                    setTimeout(() => {
                        popover.update();
                    }, 0);
                }
            }
        });

        // Add event listener to clear active popover when hidden
        el.addEventListener('hidden.bs.popover', () => {
            if (activePopover === popover) {
                activePopover = null;
            }
            
            // Unmount Vue component if it exists
            if (el._popoverApp) {
                el._popoverApp.unmount();
                el._popoverApp = null;
                el._popoverComponent = null;
            }
        });

        // Initialize click outside handler only once
        if (!clickOutsideHandler) {
            clickOutsideHandler = (e) => {
                if (activePopover) {
                    const clickedElement = e.target;
                    const isInsidePopover = clickedElement.closest('.popover') || 
                                          clickedElement.closest('[data-bs-toggle="popover"]');
                    
                    if (!isInsidePopover) {
                        activePopover.hide();
                        activePopover = null;
                    }
                }
            };
            document.addEventListener('click', clickOutsideHandler);
        }
    },
    updated(el, binding) {
        // Update popover content if it changed
        if (el._popover && binding.value !== binding.oldValue) {
            const isComponent = binding.value && typeof binding.value === 'object' && binding.value.component;
            const content = isComponent ? '<div class="popover-content-placeholder"></div>' : (typeof binding.value === 'string' ? binding.value : '');
            el._popover.setContent({ '.popover-body': content });
        }
    },
    beforeUnmount(el) {
        // Clean up popover
        if (el._popover) {
            if (activePopover === el._popover) {
                activePopover = null;
            }
            el._popover.dispose();
            delete el._popover;
        }
    }
};

// Cleanup function to remove global event listener
export const cleanupPopover = () => {
    if (clickOutsideHandler) {
        document.removeEventListener('click', clickOutsideHandler);
        clickOutsideHandler = null;
        activePopover = null;
    }
};
